import React from "react";
import {
  Box,
  Flex,
  Text,
  Spinner,
  Button,
  Badge,
  Select,
  IconButton,
  Tooltip,
} from "@radix-ui/themes";
import { CopyIcon } from "@radix-ui/react-icons";
import { parseInsightSections } from "~/utils/explain-utils";
import {
  groupSecurityExplanations,
  groupSectorExplanations,
} from "~/utils/helpers";
import SecurityExplainContent from "~/components/security-explain-content";
import { CopyButton } from "~/components/copy-button";
import InsightsHeader from "./insights-header";
import { CircularArrowUp, CircularArrowDown } from "./circular-arrows";
import SectorChart from "./sector-chart";
import {
  SectorContributionsTable,
  InlineContributionData,
  AssetClassContributionsTable,
} from "./contributions-table";
import { SecurityContributionsWithPerformance } from "./security-contributions-with-performance";

// Regex patterns for cleaning content
export const COMPANY_NAME_PREFIX_REGEX = /^\((?!https?:\/\/)[^)]+\)\s*/; // Matches (SYMBOL Company) but not (https://...)
export const SECTOR_PREFIX_REGEX = /^[A-Za-z]+\s*-\s*/; // Matches "Sector - " prefix

// Remove company name prefix like "(AAPL Apple Inc.) ..." while preserving markdown links
const removeCompanyNamePrefix = (content: string): string => {
  return content.replace(COMPANY_NAME_PREFIX_REGEX, "").trim();
};

type TabType = "positive" | "negative";
type TabConfig = {
  key: string;
  display: string;
  apiKey?: string;
};

type PortfolioTabType = string;

const processPortfolioContent = (content: string | string[]) => {
  if (Array.isArray(content)) {
    return content.map((item) => `- ${item}`).join("\n");
  }

  if (typeof content === "string") {
    try {
      // Try parsing as standard JSON
      let parsed = JSON.parse(content);
      if (Array.isArray(parsed)) {
        return parsed.map((item) => `- ${item}`).join("\n");
      }
    } catch {
      try {
        // Try with single quotes converted to double quotes
        const fixedContent = content.replace(/'/g, '"');
        const parsed = JSON.parse(fixedContent);
        if (Array.isArray(parsed)) {
          return parsed.map((item) => `- ${item}`).join("\n");
        }
      } catch {
        // Fallback to plain text formatting
        return content
          .split("\n")
          .filter((line) => line.trim())
          .map((line) => (line.startsWith("-") ? line : `- ${line}`))
          .join("\n");
      }
    }
  }

  return content || "";
};

const getTabConfiguration = (
  analysisResults: Record<string, unknown>,
  hideViewAll = false
): TabConfig[] => {
  const tabs: TabConfig[] = [];

  // AssetMark demo format - has portfolio_highlights, moderate_narrative, advanced_narrative
  if ("portfolio_highlights" in analysisResults) {
    // Talk Tracks tab with sub-tabs
    tabs.push({
      key: "talk_tracks",
      display: "Talk Tracks",
    });

    // If we have explanations from the normal endpoint, add regular portfolio tabs
    if ("explanations" in analysisResults && analysisResults.explanations) {
      tabs.push(
        {
          key: "macro",
          display: "Macro",
        },
        {
          key: "sector",
          display: "Sector",
        },
        {
          key: "security",
          display: "Security",
        }
      );
    }

    // View All tab
    if (!hideViewAll) {
      tabs.push({
        key: "all",
        display: "View All",
      });
    }

    return tabs;
  }

  // Fund explain - has nested explanations structure
  if (
    "explanations" in analysisResults &&
    typeof analysisResults.explanations === "object" &&
    analysisResults.explanations !== null
  ) {
    const explanations = analysisResults.explanations as Record<
      string,
      unknown
    >;

    // Key Developments tab
    if (
      explanations.key_developments &&
      typeof explanations.key_developments === "object" &&
      explanations.key_developments !== null
    ) {
      const keyDev = explanations.key_developments as Record<string, unknown>;
      if (typeof keyDev.summary === "string") {
        tabs.push({
          key: "key_developments",
          display: "Key Developments",
          apiKey: "key_developments",
        });
      }
    }

    if (explanations.macro) {
      tabs.push({ key: "macro", display: "Macro Overview", apiKey: "macro" });
    }

    if (
      explanations.theme_explanations &&
      typeof explanations.theme_explanations === "object" &&
      explanations.theme_explanations !== null &&
      Object.keys(explanations.theme_explanations).length > 0
    ) {
      tabs.push({
        key: "theme_explanations",
        display: "Thematic Insights",
        apiKey: "theme_explanations",
      });
    }

    if (
      explanations.sector &&
      typeof explanations.sector === "object" &&
      explanations.sector !== null
    ) {
      const sector = explanations.sector as Record<string, unknown>;
      if (sector.positive_explanation || sector.negative_explanation) {
        tabs.push({
          key: "sector",
          display: "Sector Overview",
          apiKey: "sector",
        });
      }
    }

    if (
      explanations.security &&
      typeof explanations.security === "object" &&
      explanations.security !== null
    ) {
      const security = explanations.security as Record<string, unknown>;
      if (security.positive_explanation || security.negative_explanation) {
        tabs.push({
          key: "security",
          display: "Notable Securities",
          apiKey: "security",
        });
      }
    }

    if (!hideViewAll) {
      tabs.push({ key: "all", display: "View All" });
    }
  }
  // Portfolio explain - flat structure with _positive/_negative keys
  else if (
    "macro_positive" in analysisResults ||
    "sector_positive" in analysisResults ||
    "security_positive" in analysisResults
  ) {
    if (analysisResults.macro_positive || analysisResults.macro_negative) {
      tabs.push({ key: "macro", display: "Macro Overview", apiKey: "macro" });
    }
    if (analysisResults.sector_positive || analysisResults.sector_negative) {
      tabs.push({
        key: "sector",
        display: "Sector Overview",
        apiKey: "sector",
      });
    }
    if (
      analysisResults.security_positive ||
      analysisResults.security_negative
    ) {
      tabs.push({
        key: "security",
        display: "Notable Securities",
        apiKey: "security",
      });
    }
    if (!hideViewAll) {
      tabs.push({ key: "all", display: "View All" });
    }
  }
  return tabs;
};

// TODO: Fix prop drilling with contexts
const renderTabContent = (
  tab: TabConfig,
  analysisResults: Record<string, unknown>,
  isMobileView: boolean,
  newsProvider?: string,
  symbol?: string,
  enableCopy?: boolean,
  disablePerformance?: boolean,
  newsTruncateWords?: number,
  newsLinkText?: string
) => {
  // Talk Tracks tab - render sub-tabs
  if (tab.key === "talk_tracks") {
    return null; // Will be handled separately with sub-tab rendering
  }

  // Macro tab from normal portfolio endpoint
  if (tab.key === "macro") {
    let macroContent = "";

    // Check for nested explanations format
    if ("explanations" in analysisResults) {
      const explanations = analysisResults.explanations as Record<
        string,
        unknown
      >;
      if (typeof explanations.macro === "string") {
        macroContent = explanations.macro;
      } else if (explanations.macro && typeof explanations.macro === "object") {
        const macroObj = explanations.macro as Record<string, string>;
        macroContent = `${macroObj.positive_explanation || ""}\n\n${
          macroObj.negative_explanation || ""
        }`.trim();
      }
    }
    // Check for flat format (bulletized portfolio explain)
    else {
      const macroPositive = (analysisResults.macro_positive as string) || "";
      const macroNegative = (analysisResults.macro_negative as string) || "";
      macroContent = `${macroPositive}\n\n${macroNegative}`.trim();
    }

    if (!macroContent) return null;

    return (
      <SecurityExplainContent
        content={macroContent}
        popoutNews={true}
        newsProvider={newsProvider}
        isMobileView={isMobileView}
        enableCopy={enableCopy}
        newsTruncateWords={newsTruncateWords}
        newsLinkText={newsLinkText}
      />
    );
  }

  // Sector tab from normal portfolio endpoint
  if (tab.key === "sector") {
    const componentsData = (analysisResults.normal_components ||
      analysisResults.components) as Record<string, unknown> | undefined;
    const sectorData = componentsData?.sectors as
      | Record<string, { weight: number; contribution_to_return: number }>
      | undefined;
    const sectorVisualization = analysisResults.sector_visualization;

    let positiveContent: string | string[] | undefined;
    let negativeContent: string | string[] | undefined;

    // Check for nested explanations format
    if ("explanations" in analysisResults) {
      const explanations = analysisResults.explanations as Record<
        string,
        unknown
      >;
      if (typeof explanations.sector === "string") {
        positiveContent = explanations.sector;
      } else if (
        explanations.sector &&
        typeof explanations.sector === "object"
      ) {
        const sectorObj = explanations.sector as Record<string, unknown>;
        positiveContent = sectorObj.positive_explanation as
          | string
          | string[]
          | undefined;
        negativeContent = sectorObj.negative_explanation as
          | string
          | string[]
          | undefined;
      }
    }
    // Check for flat format (bulletized portfolio explain)
    else {
      positiveContent = analysisResults.sector_positive as
        | string
        | string[]
        | undefined;
      negativeContent = analysisResults.sector_negative as
        | string
        | string[]
        | undefined;
    }

    return (
      <Flex direction="column" gap="4">
        {sectorVisualization && <SectorChart data={sectorVisualization} />}
        {sectorData && Object.keys(sectorData).length > 0 && (
          <SectorContributionsTable
            data={sectorData}
            title="Sector Breakdown"
            compact={true}
          />
        )}
        <CombinedSectorContent
          positiveContent={positiveContent}
          negativeContent={negativeContent}
          isMobileView={isMobileView}
          newsProvider={newsProvider}
          enableCopy={enableCopy}
          newsTruncateWords={newsTruncateWords}
          newsLinkText={newsLinkText}
        />
      </Flex>
    );
  }

  // Security tab from normal portfolio endpoint
  if (tab.key === "security") {
    const componentsData = (analysisResults.normal_components ||
      analysisResults.components) as Record<string, unknown> | undefined;
    const figiToSymbolName = analysisResults.figi_to_symbol_name as
      | Record<string, string>
      | undefined;
    const securityData = componentsData?.securities as
      | Record<string, { weight: number; contribution_to_return: number }>
      | undefined;

    let positiveContent: string | string[] | undefined;
    let negativeContent: string | string[] | undefined;

    // Check for nested explanations format
    if ("explanations" in analysisResults) {
      const explanations = analysisResults.explanations as Record<
        string,
        unknown
      >;
      if (typeof explanations.security === "string") {
        positiveContent = explanations.security;
      } else if (
        explanations.security &&
        typeof explanations.security === "object"
      ) {
        const securityObj = explanations.security as Record<string, unknown>;
        positiveContent = securityObj.positive_explanation as
          | string
          | string[]
          | undefined;
        negativeContent = securityObj.negative_explanation as
          | string
          | string[]
          | undefined;
      }
    }
    // Check for flat format (bulletized portfolio explain)
    else {
      positiveContent = analysisResults.security_positive as
        | string
        | string[]
        | undefined;
      negativeContent = analysisResults.security_negative as
        | string
        | string[]
        | undefined;
    }

    return (
      <Flex direction="column" gap="4">
        <CombinedSecurityContent
          positiveContent={positiveContent}
          negativeContent={negativeContent}
          isMobileView={isMobileView}
          securitiesData={securityData}
          figiToSymbolName={figiToSymbolName}
          newsProvider={newsProvider}
          enableCopy={enableCopy}
          newsTruncateWords={newsTruncateWords}
          newsLinkText={newsLinkText}
        />
      </Flex>
    );
  }

  // AssetMark demo format tabs (for sub-tabs rendering)
  if (tab.key === "highlights") {
    const highlights = analysisResults.portfolio_highlights as string[];
    if (!highlights || !Array.isArray(highlights)) return null;

    // Combine all highlights into a single copyable text
    const highlightsText = highlights
      .filter((item) => item && typeof item === "string")
      .map((item) => `• ${item}`)
      .join("\n\n");

    return (
      <Box style={{ position: "relative" }}>
        {enableCopy && highlightsText && (
          <Box
            style={{ position: "absolute", top: "0", right: "0", zIndex: 10 }}
          >
            <CopyButton content={highlightsText} newsProvider={newsProvider} />
          </Box>
        )}
        <Flex direction="column" gap="2">
          {highlights.map((item, index) => (
            <Flex key={index} gap="2" align="start">
              <Text style={{ flexShrink: 0 }}>•</Text>
              <Box style={{ lineHeight: 1.5 }}>
                <SecurityExplainContent
                  content={item}
                  popoutNews={true}
                  newsProvider={newsProvider}
                  isMobileView={isMobileView}
                  enableCopy={false}
                  newsTruncateWords={newsTruncateWords}
                  newsLinkText={newsLinkText}
                />
              </Box>
            </Flex>
          ))}
        </Flex>
      </Box>
    );
  }

  if (tab.key === "moderate") {
    const narrative = analysisResults.moderate_narrative as string;
    if (!narrative) return null;

    // Get components data for asset classes table
    const components = analysisResults.components as
      | Record<string, unknown>
      | undefined;
    const assetClasses = components?.asset_classes as
      | Record<string, { weight: number; contribution_to_return: number }>
      | undefined;

    return (
      <Flex direction="column" gap="4">
        <SecurityExplainContent
          content={narrative}
          popoutNews={true}
          newsProvider={newsProvider}
          isMobileView={isMobileView}
          enableCopy={enableCopy}
          newsTruncateWords={newsTruncateWords}
          newsLinkText={newsLinkText}
        />
        {assetClasses && Object.keys(assetClasses).length > 0 && (
          <AssetClassContributionsTable
            data={assetClasses}
            title="Asset Class Breakdown"
            displayNameLookup={{
              us_equity: "US Equity",
              developed_markets_equity: "Developed Markets Equity",
              emerging_market_equity: "Emerging Market Equity",
            }}
            compact={true}
          />
        )}
      </Flex>
    );
  }

  if (tab.key === "advanced") {
    const narrative = analysisResults.advanced_narrative as string;
    if (!narrative) return null;

    // Get components data for asset classes table
    const components = analysisResults.components as
      | Record<string, unknown>
      | undefined;
    const assetClasses = components?.asset_classes as
      | Record<string, { weight: number; contribution_to_return: number }>
      | undefined;

    return (
      <Flex direction="column" gap="4">
        <SecurityExplainContent
          content={narrative}
          popoutNews={true}
          newsProvider={newsProvider}
          isMobileView={isMobileView}
          enableCopy={enableCopy}
          newsTruncateWords={newsTruncateWords}
          newsLinkText={newsLinkText}
        />
        {assetClasses && Object.keys(assetClasses).length > 0 && (
          <AssetClassContributionsTable
            data={assetClasses}
            title="Asset Class Breakdown"
            displayNameLookup={{
              us_equity: "US Equity",
              developed_markets_equity: "Developed Markets Equity",
              emerging_market_equity: "Emerging Market Equity",
            }}
            compact={true}
          />
        )}
      </Flex>
    );
  }

  // "View All" tab - shows everything from Talk Tracks and normal portfolio data
  if (tab.key === "all") {
    const hasAssetmarkData = "portfolio_highlights" in analysisResults;
    const hasNormalData = "explanations" in analysisResults;

    return (
      <Flex direction="column" gap="6">
        {/* Talk Tracks Section */}
        {hasAssetmarkData && (
          <>
            <Box>
              <Text size="4" weight="bold" mb="4">
                Talk Tracks
              </Text>
              <Flex direction="column" gap="5">
                {/* Highlights */}
                {(() => {
                  const highlights = analysisResults.portfolio_highlights as string[];
                  if (
                    !highlights ||
                    !Array.isArray(highlights) ||
                    highlights.length === 0
                  )
                    return null;

                  return (
                    <Box>
                      <Text size="3" weight="bold" mb="3" color="gray">
                        Highlights
                      </Text>
                      <Flex direction="column" gap="2">
                        {highlights.map((item, index) => (
                          <Flex key={index} gap="2" align="start">
                            <Text style={{ flexShrink: 0 }}>•</Text>
                            <Box style={{ lineHeight: 1.5 }}>
                              <SecurityExplainContent
                                content={item}
                                popoutNews={true}
                                newsProvider={newsProvider}
                                isMobileView={isMobileView}
                                newsTruncateWords={newsTruncateWords}
                                newsLinkText={newsLinkText}
                              />
                            </Box>
                          </Flex>
                        ))}
                      </Flex>
                    </Box>
                  );
                })()}

                {/* Moderate */}
                <Box>
                  <Text size="3" weight="bold" mb="3" color="gray">
                    Moderate Narrative
                  </Text>
                  {renderTabContent(
                    { key: "moderate", display: "Moderate" },
                    analysisResults,
                    isMobileView,
                    newsProvider,
                    symbol,
                    enableCopy,
                    disablePerformance,
                    newsTruncateWords,
                    newsLinkText
                  )}
                </Box>

                {/* Advanced */}
                <Box>
                  <Text size="3" weight="bold" mb="3" color="gray">
                    Advanced Narrative
                  </Text>
                  {renderTabContent(
                    { key: "advanced", display: "Advanced" },
                    analysisResults,
                    isMobileView,
                    newsProvider,
                    symbol,
                    enableCopy,
                    disablePerformance,
                    newsTruncateWords,
                    newsLinkText
                  )}
                </Box>
              </Flex>
            </Box>
          </>
        )}

        {/* Normal Portfolio Data Section */}
        {hasNormalData && (
          <>
            <Box>
              <Text size="4" weight="bold" mb="4">
                Portfolio Analysis
              </Text>
              <Flex direction="column" gap="5">
                {/* Macro */}
                <Box>
                  <Text size="3" weight="bold" mb="3" color="gray">
                    Macro
                  </Text>
                  {renderTabContent(
                    { key: "macro", display: "Macro" },
                    analysisResults,
                    isMobileView,
                    newsProvider,
                    symbol,
                    enableCopy,
                    disablePerformance,
                    newsTruncateWords,
                    newsLinkText
                  )}
                </Box>

                {/* Sector */}
                <Box>
                  <Text size="3" weight="bold" mb="3" color="gray">
                    Sector
                  </Text>
                  {renderTabContent(
                    { key: "sector", display: "Sector" },
                    analysisResults,
                    isMobileView,
                    newsProvider,
                    symbol,
                    enableCopy,
                    disablePerformance,
                    newsTruncateWords,
                    newsLinkText
                  )}
                </Box>

                {/* Security */}
                <Box>
                  <Text size="3" weight="bold" mb="3" color="gray">
                    Security
                  </Text>
                  {renderTabContent(
                    { key: "security", display: "Security" },
                    analysisResults,
                    isMobileView,
                    newsProvider,
                    symbol,
                    enableCopy,
                    disablePerformance,
                    newsTruncateWords,
                    newsLinkText
                  )}
                </Box>
              </Flex>
            </Box>
          </>
        )}
      </Flex>
    );
  }

  if (tab.key === "key_developments") {
    if (
      "explanations" in analysisResults &&
      typeof analysisResults.explanations === "object" &&
      analysisResults.explanations !== null
    ) {
      const explanations = analysisResults.explanations as Record<
        string,
        unknown
      >;
      const keyDev = explanations.key_developments;
      if (!keyDev || typeof keyDev !== "object" || keyDev === null) return null;
      const keyDevTyped = keyDev as Record<string, unknown>;

      return (
        <Box>
          {typeof keyDevTyped.summary === "string" && (
            <Box mb="4">
              <SecurityExplainContent
                content={keyDevTyped.summary as string}
                popoutNews={true}
                newsProvider={newsProvider}
                isMobileView={isMobileView}
                newsTruncateWords={newsTruncateWords}
                newsLinkText={newsLinkText}
              />
            </Box>
          )}

          {Array.isArray(keyDevTyped.positive_explanation) &&
            keyDevTyped.positive_explanation.length > 0 && (
              <Box mb="4">
                <Text size="3" weight="bold" mb="3">
                  Tailwinds
                </Text>
                {(keyDevTyped.positive_explanation as string[]).map(
                  (item: string, index: number) => (
                    <Flex key={index} gap="2" align="start" mb="2">
                      <Box style={{ flexShrink: 0, marginTop: "4px" }}>
                        <CircularArrowUp size={20} />
                      </Box>
                      <Box style={{ lineHeight: 1.5 }}>
                        <SecurityExplainContent
                          content={item}
                          popoutNews={true}
                          newsProvider={newsProvider}
                          isMobileView={isMobileView}
                          newsTruncateWords={newsTruncateWords}
                          newsLinkText={newsLinkText}
                        />
                      </Box>
                    </Flex>
                  )
                )}
              </Box>
            )}

          {Array.isArray(keyDevTyped.negative_explanation) &&
            keyDevTyped.negative_explanation.length > 0 && (
              <Box>
                <Text size="3" weight="bold" mb="3">
                  Headwinds
                </Text>
                {(keyDevTyped.negative_explanation as string[]).map(
                  (item: string, index: number) => (
                    <Flex key={index} gap="2" align="start" mb="2">
                      <Box style={{ flexShrink: 0, marginTop: "4px" }}>
                        <CircularArrowDown size={20} />
                      </Box>
                      <Box style={{ lineHeight: 1.5 }}>
                        <SecurityExplainContent
                          content={item}
                          popoutNews={true}
                          newsProvider={newsProvider}
                          isMobileView={isMobileView}
                          newsTruncateWords={newsTruncateWords}
                          newsLinkText={newsLinkText}
                        />
                      </Box>
                    </Flex>
                  )
                )}
              </Box>
            )}
        </Box>
      );
    }
    return null;
  }

  if (tab.key === "theme_explanations") {
    if (
      "explanations" in analysisResults &&
      typeof analysisResults.explanations === "object" &&
      analysisResults.explanations !== null
    ) {
      const explanations = analysisResults.explanations as Record<
        string,
        unknown
      >;
      const themes = explanations.theme_explanations;
      if (!themes || typeof themes !== "object" || themes === null) return null;
      const themesTyped = themes as Record<string, unknown>;

      return (
        <Box>
          {Object.entries(themesTyped).map(
            ([themeName, explanation]: [string, unknown]) => (
              <details key={themeName} style={{ marginBottom: "16px" }}>
                <summary
                  style={{
                    cursor: "pointer",
                    fontWeight: "bold",
                    padding: "8px 0",
                    borderBottom: "1px solid #eee",
                    marginBottom: "8px",
                  }}
                >
                  {themeName}
                </summary>
                <Box pl="3">
                  <SecurityExplainContent
                    content={
                      typeof explanation === "string"
                        ? explanation
                        : String(explanation)
                    }
                    popoutNews={true}
                    newsProvider={newsProvider}
                    isMobileView={isMobileView}
                    newsTruncateWords={newsTruncateWords}
                    newsLinkText={newsLinkText}
                  />
                </Box>
              </details>
            )
          )}
        </Box>
      );
    }
    return null;
  }

  if (tab.key === "all") {
    const isNested = "explanations" in analysisResults;
    const explanations =
      isNested &&
      typeof analysisResults.explanations === "object" &&
      analysisResults.explanations !== null
        ? (analysisResults.explanations as Record<string, unknown>)
        : ({} as Record<string, unknown>);

    // Helper functions for type safety
    const getString = (value: unknown): string | null => {
      return typeof value === "string" && value ? value : null;
    };

    const getObject = (value: unknown): Record<string, unknown> | null => {
      return value && typeof value === "object" && value !== null
        ? (value as Record<string, unknown>)
        : null;
    };

    // Pre-compute boolean conditions
    const keyDevObj = getObject(explanations.key_developments);
    const keyDevSummary = keyDevObj && getString(keyDevObj.summary);
    const hasKeyDevSummary = Boolean(keyDevSummary);
    const macroContent = getString(explanations.macro);
    const hasMacroContent = Boolean(macroContent);
    const sectorObj = getObject(explanations.sector);
    const sectorHasContent = Boolean(
      sectorObj &&
        (sectorObj.positive_explanation || sectorObj.negative_explanation)
    );
    const securityObj = getObject(explanations.security);
    const securityHasContent = Boolean(
      securityObj &&
        (securityObj.positive_explanation || securityObj.negative_explanation)
    );

    // Use ticker symbol for fund explain chart label, default to "Fund" if not provided
    const tickerSymbol = isNested ? symbol || "Fund" : undefined;

    return (
      <Flex direction="column" gap="4">
        {hasKeyDevSummary && (
          <Box>
            <Text size="3" weight="bold" className="mb-6">
              Key Developments
            </Text>
            <SecurityExplainContent
              content={
                (explanations.key_developments as Record<string, unknown>)
                  .summary as string
              }
              popoutNews={true}
              newsProvider={newsProvider}
              isMobileView={isMobileView}
              newsTruncateWords={newsTruncateWords}
              newsLinkText={newsLinkText}
            />
          </Box>
        )}
        {hasMacroContent && (
          <Box>
            <Text size="3" weight="bold" className="mb-6">
              Macro Overview
            </Text>
            <SecurityExplainContent
              content={explanations.macro as string}
              popoutNews={true}
              newsProvider={newsProvider}
              isMobileView={isMobileView}
              newsTruncateWords={newsTruncateWords}
              newsLinkText={newsLinkText}
            />
          </Box>
        )}

        {sectorHasContent && (
          <Box>
            <Text size="3" weight="bold" className="mb-6">
              Sector Overview
            </Text>
            <Flex direction="column" gap="4">
              {analysisResults.sector_visualization && (
                <SectorChart
                  data={analysisResults.sector_visualization}
                  portfolioLabel={tickerSymbol}
                />
              )}

              {analysisResults.components?.sectors && (
                <SectorContributionsTable
                  data={analysisResults.components.sectors}
                  compact={true}
                />
              )}

              <CombinedSectorContent
                positiveContent={
                  sectorObj?.positive_explanation as string | string[]
                }
                negativeContent={
                  sectorObj?.negative_explanation as string | string[]
                }
                isMobileView={isMobileView}
                newsProvider={newsProvider}
                newsTruncateWords={newsTruncateWords}
                newsLinkText={newsLinkText}
              />
            </Flex>
          </Box>
        )}
        {securityHasContent && (
          <Box>
            <Text size="3" weight="bold" className="mb-6">
              Notable Securities
            </Text>

            <Flex direction="column" gap="4">
              {analysisResults.components?.securities && (
                <SecurityContributionsWithPerformance
                  data={analysisResults.components.securities}
                  displayNameLookup={analysisResults.figi_to_symbol_name}
                  compact={true}
                  readOnly={true}
                  showPerformance={!disablePerformance}
                  timeframe="1M"
                />
              )}

              <ViewAllSecurityContent
                positiveContent={
                  securityObj?.positive_explanation as string | string[]
                }
                negativeContent={
                  securityObj?.negative_explanation as string | string[]
                }
                isMobileView={isMobileView}
                securitiesData={analysisResults.components?.securities}
                figiToSymbolName={analysisResults.figi_to_symbol_name}
                newsProvider={newsProvider}
                enableCopy={enableCopy}
                newsTruncateWords={newsTruncateWords}
                newsLinkText={newsLinkText}
              />
            </Flex>
          </Box>
        )}

        {/* Portfolio explain sections */}
        {!isNested &&
          (analysisResults.macro_positive ||
            analysisResults.macro_negative) && (
            <Box>
              <Text size="3" weight="bold" className="mb-4">
                Macro Overview
              </Text>
              <PortfolioContentWithArrows
                content={[
                  ...(analysisResults.macro_positive
                    ? [analysisResults.macro_positive as string]
                    : []),
                  ...(analysisResults.macro_negative
                    ? [analysisResults.macro_negative as string]
                    : []),
                ]}
                isPositive={true}
                isMobileView={isMobileView}
                newsTruncateWords={newsTruncateWords}
                newsLinkText={newsLinkText}
              />
            </Box>
          )}

        {!isNested &&
          (analysisResults.sector_positive ||
            analysisResults.sector_negative) && (
            <Box>
              <Text size="3" weight="bold" className="mb-4">
                Sector Overview
              </Text>
              <Flex direction="column" gap="4">
                {analysisResults.sector_visualization && (
                  <SectorChart
                    data={analysisResults.sector_visualization}
                    portfolioLabel="Portfolio"
                  />
                )}

                {analysisResults.components?.sectors && (
                  <SectorContributionsTable
                    data={analysisResults.components.sectors}
                    compact={true}
                  />
                )}

                <Box>
                  {analysisResults.sector_positive && (
                    <Box mb="2">
                      <PortfolioContentWithArrows
                        content={
                          analysisResults.sector_positive as string | string[]
                        }
                        isPositive={true}
                        isMobileView={isMobileView}
                        newsProvider={newsProvider}
                        newsTruncateWords={newsTruncateWords}
                        newsLinkText={newsLinkText}
                        isSectorContent={true}
                      />
                    </Box>
                  )}
                  {analysisResults.sector_negative && (
                    <Box>
                      <PortfolioContentWithArrows
                        content={
                          analysisResults.sector_negative as string | string[]
                        }
                        isPositive={false}
                        isMobileView={isMobileView}
                        newsProvider={newsProvider}
                        newsTruncateWords={newsTruncateWords}
                        newsLinkText={newsLinkText}
                        isSectorContent={true}
                      />
                    </Box>
                  )}
                </Box>
              </Flex>
            </Box>
          )}

        {!isNested &&
          (analysisResults.security_positive ||
            analysisResults.security_negative) && (
            <Box>
              <Text size="3" weight="bold" className="mb-4">
                Notable Securities
              </Text>

              <Flex direction="column" gap="4">
                {analysisResults.components?.securities && (
                  <SecurityContributionsWithPerformance
                    data={analysisResults.components.securities}
                    displayNameLookup={analysisResults.figi_to_symbol_name}
                    compact={true}
                    readOnly={true}
                    showPerformance={!disablePerformance}
                    timeframe="1M"
                  />
                )}

                <ViewAllSecurityContent
                  positiveContent={
                    analysisResults.security_positive as string | string[]
                  }
                  negativeContent={
                    analysisResults.security_negative as string | string[]
                  }
                  isMobileView={isMobileView}
                  securitiesData={analysisResults.components?.securities}
                  figiToSymbolName={analysisResults.figi_to_symbol_name}
                  newsProvider={newsProvider}
                  enableCopy={enableCopy}
                  newsTruncateWords={newsTruncateWords}
                  newsLinkText={newsLinkText}
                />
              </Flex>
            </Box>
          )}
      </Flex>
    );
  }

  // Handle individual sections (macro, sector, security)
  if (tab.apiKey) {
    const isNested = "explanations" in analysisResults;
    const explanations =
      isNested &&
      typeof analysisResults.explanations === "object" &&
      analysisResults.explanations !== null
        ? (analysisResults.explanations as Record<string, unknown>)
        : ({} as Record<string, unknown>);

    // Use ticker symbol for fund explain chart label, default to "Fund" if not provided
    const tickerSymbol = isNested ? symbol || "Fund" : undefined;

    // Macro tab - string for fund, pos/neg arrays for portfolio
    if (tab.apiKey === "macro") {
      if (isNested) {
        // Fund explain: simple string
        const macroContent = explanations.macro;
        if (typeof macroContent === "string" && macroContent) {
          return (
            <SecurityExplainContent
              content={macroContent}
              popoutNews={true}
              newsProvider={newsProvider}
              isMobileView={isMobileView}
              newsTruncateWords={newsTruncateWords}
              newsLinkText={newsLinkText}
            />
          );
        }
      } else {
        // Portfolio explain: positive/negative arrays
        return (
          <Box>
            {Boolean(analysisResults.macro_positive) && (
              <Box mb="2">
                <PortfolioContentWithArrows
                  content={analysisResults.macro_positive as string | string[]}
                  isPositive={true}
                  isMobileView={isMobileView}
                  newsProvider={newsProvider}
                  newsTruncateWords={newsTruncateWords}
                  newsLinkText={newsLinkText}
                />
              </Box>
            )}
            {Boolean(analysisResults.macro_negative) && (
              <Box>
                <PortfolioContentWithArrows
                  content={analysisResults.macro_negative as string | string[]}
                  isPositive={false}
                  isMobileView={isMobileView}
                  newsProvider={newsProvider}
                  newsTruncateWords={newsTruncateWords}
                  newsLinkText={newsLinkText}
                />
              </Box>
            )}
          </Box>
        );
      }
      return null;
    }

    // Sector and security tabs use positive/negative structure
    const getContent = (
      type: "positive" | "negative"
    ): string | string[] | undefined => {
      if (isNested && tab.apiKey) {
        const section = explanations[tab.apiKey];
        if (section && typeof section === "object" && section !== null) {
          const sectionTyped = section as Record<string, unknown>;
          return sectionTyped[`${type}_explanation`] as
            | string
            | string[]
            | undefined;
        }
      } else if (tab.apiKey) {
        return analysisResults[`${tab.apiKey}_${type}`] as
          | string
          | string[]
          | undefined;
      }
      return undefined;
    };

    if (tab.apiKey === "security") {
      return (
        <Flex direction="column" gap="4">
          <CombinedSecurityContent
            positiveContent={getContent("positive")}
            negativeContent={getContent("negative")}
            isMobileView={isMobileView}
            securitiesData={analysisResults.components?.securities}
            figiToSymbolName={analysisResults.figi_to_symbol_name}
            newsProvider={newsProvider}
            enableCopy={enableCopy}
            newsTruncateWords={newsTruncateWords}
            newsLinkText={newsLinkText}
          />
        </Flex>
      );
    }

    if (tab.apiKey === "sector") {
      return (
        <Flex direction="column" gap="4">
          {analysisResults.sector_visualization && (
            <SectorChart
              data={analysisResults.sector_visualization}
              portfolioLabel={isNested ? tickerSymbol : "Portfolio"}
            />
          )}

          {analysisResults.components?.sectors && (
            <SectorContributionsTable
              data={analysisResults.components.sectors}
              compact={true}
            />
          )}

          <CombinedSectorContent
            positiveContent={getContent("positive")}
            negativeContent={getContent("negative")}
            isMobileView={isMobileView}
            newsProvider={newsProvider}
            newsTruncateWords={newsTruncateWords}
            newsLinkText={newsLinkText}
          />
        </Flex>
      );
    }

    return (
      <Box>
        {getContent("positive") && (
          <PortfolioContentWithArrows
            content={getContent("positive")!}
            isPositive={true}
            isMobileView={isMobileView}
            newsTruncateWords={newsTruncateWords}
            newsLinkText={newsLinkText}
          />
        )}
        {getContent("negative") && (
          <PortfolioContentWithArrows
            content={getContent("negative")!}
            isPositive={false}
            isMobileView={isMobileView}
            newsTruncateWords={newsTruncateWords}
            newsLinkText={newsLinkText}
          />
        )}
      </Box>
    );
  }

  return null;
};

// Export for testing
export { getTabConfiguration, renderTabContent };

const CombinedSectorContent = ({
  positiveContent,
  negativeContent,
  isMobileView,
  newsProvider = "mtnews",
  enableCopy = false,
  newsTruncateWords,
  newsLinkText,
}: {
  positiveContent?: string | string[];
  negativeContent?: string | string[];
  isMobileView: boolean;
  newsProvider?: string;
  enableCopy?: boolean;
  newsTruncateWords?: number;
  newsLinkText?: string;
}) => {
  // Process content into arrays
  const processContent = (content: string | string[] | undefined): string[] => {
    if (!content) return [];
    if (Array.isArray(content)) return content;
    return content.split("\n").filter((line) => line.trim());
  };

  const positiveItems = processContent(positiveContent);
  const negativeItems = processContent(negativeContent);

  return (
    <Box>
      {/* Tailwinds section */}
      {positiveItems.length > 0 && (
        <Box className="mb-3">
          <Text size="2" weight="bold" color="gray" className="mb-3">
            TAILWINDS
          </Text>
          {positiveItems.map((item, index) => {
            const cleanContent = item.replace(SECTOR_PREFIX_REGEX, "").trim();
            return (
              <Flex key={`pos-${index}`} gap="2" align="start" className="mb-2">
                <Box style={{ flexShrink: 0, marginTop: "4px" }}>
                  <CircularArrowUp size={20} />
                </Box>
                <Box style={{ lineHeight: 1.5 }}>
                  <SecurityExplainContent
                    content={cleanContent}
                    popoutNews={true}
                    newsProvider={newsProvider}
                    isMobileView={isMobileView}
                    newsTruncateWords={newsTruncateWords}
                    newsLinkText={newsLinkText}
                  />
                </Box>
              </Flex>
            );
          })}
          {enableCopy && (
            <Box className="mt-2">
              <CopyButton
                content={positiveItems
                  .map(
                    (item) =>
                      `- ${item.replace(SECTOR_PREFIX_REGEX, "").trim()}`
                  )
                  .join("\n")}
                newsProvider={newsProvider}
              />
            </Box>
          )}
        </Box>
      )}

      {/* Headwinds */}
      {negativeItems.length > 0 && (
        <Box>
          <Text size="2" weight="bold" color="gray" className="mb-3">
            HEADWINDS
          </Text>
          {negativeItems.map((item, index) => {
            const cleanContent = item.replace(SECTOR_PREFIX_REGEX, "").trim();
            return (
              <Flex key={`neg-${index}`} gap="2" align="start" className="mb-2">
                <Box style={{ flexShrink: 0, marginTop: "4px" }}>
                  <CircularArrowDown size={20} />
                </Box>
                <Box style={{ lineHeight: 1.5 }}>
                  <SecurityExplainContent
                    content={cleanContent}
                    popoutNews={true}
                    newsProvider={newsProvider}
                    isMobileView={isMobileView}
                    newsTruncateWords={newsTruncateWords}
                    newsLinkText={newsLinkText}
                  />
                </Box>
              </Flex>
            );
          })}
          {enableCopy && (
            <Box className="mt-2">
              <CopyButton
                content={negativeItems
                  .map(
                    (item) =>
                      `- ${item.replace(SECTOR_PREFIX_REGEX, "").trim()}`
                  )
                  .join("\n")}
                newsProvider={newsProvider}
              />
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

const ViewAllSecurityContent = ({
  positiveContent,
  negativeContent,
  isMobileView,
  securitiesData,
  figiToSymbolName,
  newsProvider = "mtnews",
  enableCopy = false,
  newsTruncateWords,
  newsLinkText,
}: {
  positiveContent?: string | string[];
  negativeContent?: string | string[];
  isMobileView: boolean;
  securitiesData?: {
    [figi: string]: { contribution_to_return: number; weight: number };
  };
  figiToSymbolName?: { [figi: string]: string };
  newsProvider?: string;
  enableCopy?: boolean;
  newsTruncateWords?: number;
  newsLinkText?: string;
}) => {
  // Group both positive and negative content by company
  const positiveGrouped = positiveContent
    ? groupSecurityExplanations(positiveContent)
    : {};
  const negativeGrouped = negativeContent
    ? groupSecurityExplanations(negativeContent)
    : {};

  // Helper function to find FIGI for a company name
  const findFigiForCompany = React.useCallback(
    (companyName: string): string | null => {
      if (!figiToSymbolName) return null;

      // Extract ticker symbol from company name using regex
      // Look for 3-5 uppercase letters, possibly with numbers
      const tickerMatch = companyName.match(/\b([A-Z]{2,5})\b/);
      const extractedTicker = tickerMatch ? tickerMatch[1] : null;

      if (extractedTicker) {
        // Find FIGI where the symbol name starts with this ticker
        const figiByTicker = Object.keys(figiToSymbolName).find((figi) => {
          const symbolName = figiToSymbolName[figi];
          const symbolTicker = symbolName.split(" ")[0]; // Get first part (ticker)
          return symbolTicker === extractedTicker;
        });

        if (figiByTicker) {
          return figiByTicker;
        }
      }

      // Fallback: try partial company name match
      const partialMatch = Object.keys(figiToSymbolName).find((figi) => {
        const symbolName = figiToSymbolName[figi].toLowerCase();
        const searchName = companyName.toLowerCase();

        // Extract company name part (after ticker symbol)
        const companyPart = symbolName.split(" ").slice(1).join(" ");

        // Check if company name matches the company part
        return (
          companyPart.includes(searchName) || searchName.includes(companyPart)
        );
      });

      return partialMatch || null;
    },
    [figiToSymbolName]
  );

  // Helper function to get real data for a company
  const getCompanyData = (companyName: string) => {
    const figi = findFigiForCompany(companyName);
    if (!figi || !securitiesData?.[figi]) {
      return { weight: null, contribution: null };
    }

    const data = securitiesData[figi];
    return {
      weight: data.weight,
      contribution: data.contribution_to_return,
    };
  };

  // Combine all companies from both positive and negative
  const allCompanies = new Set([
    ...Object.keys(positiveGrouped),
    ...Object.keys(negativeGrouped),
  ]);

  return (
    <Box>
      {Array.from(allCompanies).map((companyName) => {
        const { weight, contribution } = getCompanyData(companyName);

        return (
          <Box key={companyName} className="mb-4">
            <Flex justify="between" align="center" className="mb-3">
              <Text size="2" weight="bold">
                {companyName}
              </Text>
              <InlineContributionData
                weight={weight}
                contribution={contribution}
                compact={true}
              />
            </Flex>

            {/* Tailwinds for this company */}
            {positiveGrouped[companyName] &&
              positiveGrouped[companyName].length > 0 && (
                <Box className="mb-3">
                  <Text size="1" weight="bold" color="gray" className="mb-3">
                    TAILWINDS
                  </Text>
                  {positiveGrouped[companyName].map((item, index) => {
                    const cleanContent = removeCompanyNamePrefix(item);
                    return (
                      <Flex
                        key={`pos-${index}`}
                        gap="2"
                        align="start"
                        className="mb-2"
                      >
                        <Box style={{ flexShrink: 0, marginTop: "4px" }}>
                          <CircularArrowUp size={20} />
                        </Box>
                        <Box style={{ lineHeight: 1.5 }}>
                          <SecurityExplainContent
                            content={cleanContent}
                            popoutNews={true}
                            newsProvider={newsProvider}
                            isMobileView={isMobileView}
                            newsTruncateWords={newsTruncateWords}
                            newsLinkText={newsLinkText}
                          />
                        </Box>
                      </Flex>
                    );
                  })}
                  {enableCopy && (
                    <Box className="mt-2">
                      <CopyButton
                        content={positiveGrouped[companyName]
                          .map((item) => `- ${removeCompanyNamePrefix(item)}`)
                          .join("\n")}
                        newsProvider={newsProvider}
                      />
                    </Box>
                  )}
                </Box>
              )}

            {/* Headwinds for this company */}
            {negativeGrouped[companyName] &&
              negativeGrouped[companyName].length > 0 && (
                <Box>
                  <Text size="1" weight="bold" color="gray" className="mb-3">
                    HEADWINDS
                  </Text>
                  {negativeGrouped[companyName].map((item, index) => {
                    const cleanContent = removeCompanyNamePrefix(item);
                    return (
                      <Flex
                        key={`neg-${index}`}
                        gap="2"
                        align="start"
                        className="mb-2"
                      >
                        <Box style={{ flexShrink: 0, marginTop: "4px" }}>
                          <CircularArrowDown size={20} />
                        </Box>
                        <Box style={{ lineHeight: 1.5 }}>
                          <SecurityExplainContent
                            content={cleanContent}
                            popoutNews={true}
                            newsProvider={newsProvider}
                            isMobileView={isMobileView}
                            newsTruncateWords={newsTruncateWords}
                            newsLinkText={newsLinkText}
                          />
                        </Box>
                      </Flex>
                    );
                  })}
                  {enableCopy && (
                    <Box className="mt-2">
                      <CopyButton
                        content={negativeGrouped[companyName]
                          .map((item) => `- ${removeCompanyNamePrefix(item)}`)
                          .join("\n")}
                        newsProvider={newsProvider}
                      />
                    </Box>
                  )}
                </Box>
              )}
          </Box>
        );
      })}
    </Box>
  );
};

const SecurityContentWithTable = ({
  positiveContent,
  negativeContent,
  isMobileView,
  securitiesData,
  figiToSymbolName,
  newsProvider = "mtnews",
  enableCopy = false,
  newsTruncateWords,
  newsLinkText,
}: {
  positiveContent?: string | string[];
  negativeContent?: string | string[];
  isMobileView: boolean;
  securitiesData?: {
    [figi: string]: { contribution_to_return: number; weight: number };
  };
  figiToSymbolName?: { [figi: string]: string };
  newsProvider?: string;
  enableCopy?: boolean;
  newsTruncateWords?: number;
  newsLinkText?: string;
}) => {
  const [selectedCompany, setSelectedCompany] = React.useState<string>("");

  // Group both positive and negative content by company
  const positiveGrouped = React.useMemo(
    () => (positiveContent ? groupSecurityExplanations(positiveContent) : {}),
    [positiveContent]
  );
  const negativeGrouped = React.useMemo(
    () => (negativeContent ? groupSecurityExplanations(negativeContent) : {}),
    [negativeContent]
  );

  // Combine all companies from both positive and negative
  const allCompanies = React.useMemo(
    () =>
      new Set([
        ...Object.keys(positiveGrouped),
        ...Object.keys(negativeGrouped),
      ]),
    [positiveGrouped, negativeGrouped]
  );

  // Create explanation counts for the table
  const positiveExplanationCounts = React.useMemo(() => {
    const counts: Record<string, number> = {};
    Object.entries(positiveGrouped).forEach(([company, explanations]) => {
      counts[company] = explanations.length;
    });
    return counts;
  }, [positiveGrouped]);

  const negativeExplanationCounts = React.useMemo(() => {
    const counts: Record<string, number> = {};
    Object.entries(negativeGrouped).forEach(([company, explanations]) => {
      counts[company] = explanations.length;
    });
    return counts;
  }, [negativeGrouped]);

  // Helper function to find FIGI for a company name
  const findFigiForCompany = React.useCallback(
    (companyName: string): string | null => {
      if (!figiToSymbolName) return null;

      // Extract ticker symbol from company name using regex
      const tickerMatch = companyName.match(/\b([A-Z]{2,5})\b/);
      const extractedTicker = tickerMatch ? tickerMatch[1] : null;

      if (extractedTicker) {
        // Find FIGI where the symbol name starts with this ticker
        const figiByTicker = Object.keys(figiToSymbolName).find((figi) => {
          const symbolName = figiToSymbolName[figi];
          const symbolTicker = symbolName.split(" ")[0]; // Get first part (ticker)
          return symbolTicker === extractedTicker;
        });

        if (figiByTicker) {
          return figiByTicker;
        }
      }

      // Fallback: try partial company name match
      const partialMatch = Object.keys(figiToSymbolName).find((figi) => {
        const symbolName = figiToSymbolName[figi].toLowerCase();
        const searchName = companyName.toLowerCase();

        // Extract company name part (after ticker symbol)
        const companyPart = symbolName.split(" ").slice(1).join(" ");

        // Check if company name matches the company part
        return (
          companyPart.includes(searchName) || searchName.includes(companyPart)
        );
      });

      return partialMatch || null;
    },
    [figiToSymbolName]
  );

  // Create a mapping from company names to their FIGIs for the table
  const companyToFigiMap = React.useMemo(() => {
    const map: Record<string, string> = {};
    Array.from(allCompanies).forEach((companyName) => {
      const figi = findFigiForCompany(companyName);
      if (figi) {
        map[companyName] = figi;
      }
    });
    return map;
  }, [allCompanies, findFigiForCompany]);

  // Create table data using FIGIs as keys for more accurate lookups
  const tableData = React.useMemo(() => {
    const data: Record<
      string,
      { contribution_to_return: number; weight: number }
    > = {};

    Array.from(allCompanies).forEach((companyName) => {
      const figi = companyToFigiMap[companyName];
      if (figi && securitiesData?.[figi]) {
        data[figi] = securitiesData[figi];
      }
    });

    return data;
  }, [allCompanies, companyToFigiMap, securitiesData]);

  // Create a reverse mapping from FIGI to company name for table selection
  const figiToCompanyMap = React.useMemo(() => {
    const map: Record<string, string> = {};
    Object.entries(companyToFigiMap).forEach(([companyName, figi]) => {
      map[figi] = companyName;
    });
    return map;
  }, [companyToFigiMap]);

  // Start with no selection - user must click to expand

  // Map positive/negative counts by FIGI for the table
  const figiPositiveExplanationCounts = React.useMemo(() => {
    const counts: Record<string, number> = {};
    Object.entries(positiveExplanationCounts).forEach(
      ([companyName, count]) => {
        const figi = companyToFigiMap[companyName];
        if (figi) {
          counts[figi] = count;
        }
      }
    );
    return counts;
  }, [positiveExplanationCounts, companyToFigiMap]);

  const figiNegativeExplanationCounts = React.useMemo(() => {
    const counts: Record<string, number> = {};
    Object.entries(negativeExplanationCounts).forEach(
      ([companyName, count]) => {
        const figi = companyToFigiMap[companyName];
        if (figi) {
          counts[figi] = count;
        }
      }
    );
    return counts;
  }, [negativeExplanationCounts, companyToFigiMap]);

  // Handle table row selection (convert FIGI back to company name)
  const handleTableRowClick = (figi: string) => {
    const companyName = figiToCompanyMap[figi];
    if (companyName) {
      if (selectedCompany === "view-all") {
        // If view-all is active, clicking a row should collapse view-all and select just that row
        setSelectedCompany(companyName);
      } else {
        // Normal toggle behavior
        setSelectedCompany(selectedCompany === companyName ? "" : companyName);
      }
    }
  };

  // Handle view all toggle
  const handleViewAllClick = () => {
    setSelectedCompany(selectedCompany === "view-all" ? "" : "view-all");
  };

  // Determine which rows should be expanded
  const getExpandedRowKey = () => {
    if (selectedCompany === "view-all") {
      return "all"; // Special key to expand all rows
    }
    return selectedFigi; // Single row expansion
  };

  // Get selected FIGI for table highlighting
  const selectedFigi = companyToFigiMap[selectedCompany];

  const renderCompanyExplanation = React.useCallback(
    (companyName: string) => {
      return (
        <Box
          p="4"
          style={{
            backgroundColor: "var(--gray-2)",
            borderTop: "1px solid var(--gray-6)",
          }}
        >
          {positiveGrouped[companyName] &&
            positiveGrouped[companyName].length > 0 && (
              <Box className="mb-3">
                <Text size="1" weight="bold" color="gray" className="mb-3">
                  TAILWINDS
                </Text>
                {positiveGrouped[companyName].map((item, index) => {
                  const cleanContent = removeCompanyNamePrefix(item);
                  return (
                    <Flex
                      key={`pos-${index}`}
                      gap="2"
                      align="start"
                      className="mb-2"
                    >
                      <Box style={{ flexShrink: 0, marginTop: "4px" }}>
                        <CircularArrowUp size={20} />
                      </Box>
                      <Box style={{ lineHeight: 1.5 }}>
                        <SecurityExplainContent
                          content={cleanContent}
                          popoutNews={true}
                          newsProvider={newsProvider}
                          isMobileView={isMobileView}
                          newsTruncateWords={newsTruncateWords}
                          newsLinkText={newsLinkText}
                        />
                      </Box>
                    </Flex>
                  );
                })}
                {enableCopy && (
                  <Box className="mt-2">
                    <CopyButton
                      content={positiveGrouped[companyName]
                        .map((item) => `- ${removeCompanyNamePrefix(item)}`)
                        .join("\n")}
                      newsProvider={newsProvider}
                    />
                  </Box>
                )}
              </Box>
            )}

          {negativeGrouped[companyName] &&
            negativeGrouped[companyName].length > 0 && (
              <Box>
                <Text size="1" weight="bold" color="gray" className="mb-3">
                  HEADWINDS
                </Text>
                {negativeGrouped[companyName].map((item, index) => {
                  const cleanContent = removeCompanyNamePrefix(item);
                  return (
                    <Flex
                      key={`neg-${index}`}
                      gap="2"
                      align="start"
                      className="mb-2"
                    >
                      <Box style={{ flexShrink: 0, marginTop: "4px" }}>
                        <CircularArrowDown size={20} />
                      </Box>
                      <Box style={{ lineHeight: 1.5 }}>
                        <SecurityExplainContent
                          content={cleanContent}
                          popoutNews={true}
                          newsProvider={newsProvider}
                          isMobileView={isMobileView}
                          newsTruncateWords={newsTruncateWords}
                          newsLinkText={newsLinkText}
                        />
                      </Box>
                    </Flex>
                  );
                })}
                {enableCopy && (
                  <Box className="mt-2">
                    <CopyButton
                      content={negativeGrouped[companyName]
                        .map((item) => `- ${removeCompanyNamePrefix(item)}`)
                        .join("\n")}
                      newsProvider={newsProvider}
                    />
                  </Box>
                )}
              </Box>
            )}
        </Box>
      );
    },
    [
      positiveGrouped,
      negativeGrouped,
      isMobileView,
      newsProvider,
      enableCopy,
      newsTruncateWords,
      newsLinkText,
    ]
  );

  // Create expanded content for the table
  const expandedContent = React.useMemo(() => {
    const content: Record<string, React.ReactNode> = {};

    Object.keys(tableData).forEach((figi) => {
      const companyName = figiToCompanyMap[figi];
      if (companyName) {
        content[figi] = renderCompanyExplanation(companyName);
      }
    });

    return content;
  }, [tableData, figiToCompanyMap, renderCompanyExplanation]);

  return (
    <Flex direction="column" gap="4">
      {Object.keys(tableData).length > 0 && (
        <SecurityContributionsWithPerformance
          data={tableData}
          displayNameLookup={figiToSymbolName}
          selectedRow={selectedFigi}
          onRowClick={handleTableRowClick}
          positiveExplanationCounts={figiPositiveExplanationCounts}
          negativeExplanationCounts={figiNegativeExplanationCounts}
          expandedContent={expandedContent}
          expandedRowKey={getExpandedRowKey()}
          compact={true}
          showPerformance={true}
          timeframe="1M"
        />
      )}

      {allCompanies.size > 1 && (
        <Flex justify="end">
          <Button
            variant={selectedCompany === "view-all" ? "solid" : "outline"}
            size="2"
            onClick={handleViewAllClick}
          >
            {selectedCompany === "view-all" ? "Collapse All" : "Expand All"}
          </Button>
        </Flex>
      )}
    </Flex>
  );
};

const CombinedSecurityContent = ({
  positiveContent,
  negativeContent,
  isMobileView,
  securitiesData,
  figiToSymbolName,
  newsProvider = "mtnews",
  enableCopy = false,
  newsTruncateWords,
  newsLinkText,
}: {
  positiveContent?: string | string[];
  negativeContent?: string | string[];
  isMobileView: boolean;
  securitiesData?: {
    [figi: string]: { contribution_to_return: number; weight: number };
  };
  figiToSymbolName?: { [figi: string]: string };
  newsProvider?: string;
  enableCopy?: boolean;
  newsTruncateWords?: number;
  newsLinkText?: string;
}) => {
  return (
    <SecurityContentWithTable
      positiveContent={positiveContent}
      negativeContent={negativeContent}
      isMobileView={isMobileView}
      securitiesData={securitiesData}
      figiToSymbolName={figiToSymbolName}
      newsProvider={newsProvider}
      enableCopy={enableCopy}
      newsTruncateWords={newsTruncateWords}
      newsLinkText={newsLinkText}
    />
  );
};

const PortfolioContentWithArrows = ({
  content,
  isPositive,
  isMobileView,
  isSecurityContent = false,
  isSectorContent = false,
  newsProvider = "mtnews",
  newsTruncateWords,
  newsLinkText,
}: {
  content: string | string[];
  isPositive: boolean;
  isMobileView: boolean;
  isSecurityContent?: boolean;
  isSectorContent?: boolean;
  newsProvider?: string;
  newsTruncateWords?: number;
  newsLinkText?: string;
}) => {
  // For security content, group by company names
  if (isSecurityContent) {
    const grouped = groupSecurityExplanations(content);

    return (
      <Box>
        {Object.entries(grouped).map(([companyName, items]) => (
          <Box key={companyName} mb="3">
            <Flex direction="column" gap="2" mb="2">
              <Flex justify="between" align="center">
                <Text size="2" weight="bold">
                  {companyName}
                </Text>
                <Flex gap="2" align="center">
                  <Badge variant="surface" size="1">
                    Weight: 8.5%
                  </Badge>
                  <Badge variant="surface" size="1" color="green">
                    Contribution: +0.3%
                  </Badge>
                </Flex>
              </Flex>
            </Flex>
            {items.map((item, index) => {
              const cleanContent = item.replace(/\([^)]+\)/, "").trim();
              return (
                <Flex key={index} gap="2" align="start" mb="2" pl="2">
                  <Box style={{ flexShrink: 0, marginTop: "4px" }}>
                    {isPositive ? (
                      <CircularArrowUp size={20} />
                    ) : (
                      <CircularArrowDown size={20} />
                    )}
                  </Box>
                  <Box style={{ lineHeight: 1.5 }}>
                    <SecurityExplainContent
                      content={cleanContent}
                      popoutNews={true}
                      newsProvider={newsProvider}
                      isMobileView={isMobileView}
                      newsTruncateWords={newsTruncateWords}
                      newsLinkText={newsLinkText}
                    />
                  </Box>
                </Flex>
              );
            })}
          </Box>
        ))}
      </Box>
    );
  }

  // For sector content, group by sector names
  if (isSectorContent) {
    const grouped = groupSectorExplanations(content);

    return (
      <Box>
        {Object.entries(grouped).map(([sectorName, items]) => (
          <Box key={sectorName} mb="3">
            <Text size="2" weight="bold" className="mb-2">
              {sectorName}
            </Text>
            {items.map((item, index) => {
              const cleanContent = item.replace(/^[A-Za-z]+\s*-\s*/, "").trim();
              return (
                <Flex key={index} gap="2" align="start" mb="2" pl="2">
                  <Box style={{ flexShrink: 0, marginTop: "4px" }}>
                    {isPositive ? (
                      <CircularArrowUp size={20} />
                    ) : (
                      <CircularArrowDown size={20} />
                    )}
                  </Box>
                  <Box style={{ lineHeight: 1.5 }}>
                    <SecurityExplainContent
                      content={cleanContent}
                      popoutNews={true}
                      newsProvider={newsProvider}
                      isMobileView={isMobileView}
                      newsTruncateWords={newsTruncateWords}
                      newsLinkText={newsLinkText}
                    />
                  </Box>
                </Flex>
              );
            })}
          </Box>
        ))}
      </Box>
    );
  }

  const processed = processPortfolioContent(content);

  return (
    <Box>
      {processed
        .split("\n")
        .filter((line) => line.trim())
        .map((line, index) => {
          const isBullet = /^[\s]*[-*•]/.test(line);
          if (isBullet) {
            const bulletContent = line.replace(/^[\s]*[-*•]\s*/, "");
            return (
              <Flex key={index} gap="2" align="start" mb="2">
                <Box style={{ flexShrink: 0, marginTop: "4px" }}>
                  {isPositive ? (
                    <CircularArrowUp size={20} />
                  ) : (
                    <CircularArrowDown size={20} />
                  )}
                </Box>
                <Box style={{ lineHeight: 1.5 }}>
                  <SecurityExplainContent
                    content={bulletContent}
                    popoutNews={true}
                    newsProvider={newsProvider}
                    isMobileView={isMobileView}
                    newsTruncateWords={newsTruncateWords}
                    newsLinkText={newsLinkText}
                  />
                </Box>
              </Flex>
            );
          } else if (line.trim()) {
            return (
              <Flex key={index} gap="2" align="start" mb="2">
                <Box style={{ flexShrink: 0, marginTop: "4px" }}>
                  {isPositive ? (
                    <CircularArrowUp size={20} />
                  ) : (
                    <CircularArrowDown size={20} />
                  )}
                </Box>
                <Box style={{ lineHeight: 1.5 }}>
                  <SecurityExplainContent
                    content={line}
                    popoutNews={true}
                    newsProvider={newsProvider}
                    isMobileView={isMobileView}
                    newsTruncateWords={newsTruncateWords}
                    newsLinkText={newsLinkText}
                  />
                </Box>
              </Flex>
            );
          }
          return null;
        })}
    </Box>
  );
};

interface TabButtonProps {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}

const TabButton: React.FC<TabButtonProps> = ({ active, onClick, children }) => (
  <Button
    variant={active ? "solid" : "outline"}
    color={active ? undefined : "gray"}
    size="2"
    onClick={onClick}
  >
    {children}
  </Button>
);

interface InsightsContentProps {
  analysisLoading: boolean;
  analysisResults: Record<string, unknown> | null;
  analysisError: string | null;
  activeTab: TabType;
  onTabChange: (tab: TabType) => void;
  name?: string;
  symbol?: string; // Ticker symbol for labeling charts
  timeframe?: string;
  subtitle?: string; // Custom subtitle for insights header
  showApiCall?: boolean;
  onApiCallToggle?: () => void;
  isMobileView?: boolean;
  newsProvider?: string;
  enableCopy?: boolean;
  hideViewAll?: boolean; // Hide "View All" tab for embed views
  disablePerformance?: boolean; // Disable performance data fetching to prevent layout shifts
  loadingProgress?: number; // Progress (0-100) for progress bar
  traceId?: string; // Trace ID from backend response for debugging
  newsTruncateWords?: number; // Truncate news popover to this many words
  newsLinkText?: string; // Custom text for news popover link button
  isFund?: boolean; // Whether the entity being explained is a fund/ETF
}

export default function InsightsContent({
  analysisLoading,
  analysisResults,
  analysisError,
  activeTab,
  onTabChange,
  name,
  symbol,
  timeframe = "auto",
  subtitle,
  showApiCall = false,
  onApiCallToggle,
  isMobileView = false,
  newsProvider = "mtnews",
  enableCopy = false,
  hideViewAll = false,
  disablePerformance = false,
  loadingProgress,
  traceId,
  newsTruncateWords,
  newsLinkText,
  isFund = false,
}: InsightsContentProps) {
  const [portfolioTab, setPortfolioTab] = React.useState<PortfolioTabType>("");
  const [talkTracksSubTab, setTalkTracksSubTab] = React.useState<string>(
    "highlights"
  );
  const [traceCopied, setTraceCopied] = React.useState(false);

  const handleCopyTraceId = React.useCallback(() => {
    if (traceId) {
      navigator.clipboard.writeText(traceId);
      setTraceCopied(true);
      setTimeout(() => setTraceCopied(false), 2000);
    }
  }, [traceId]);

  // Set default tab based on configuration - use first tab
  React.useEffect(() => {
    if (analysisResults) {
      const config = getTabConfiguration(analysisResults, hideViewAll);
      if (config.length > 0) {
        // Default to first tab (which is "talk_tracks" for AssetMark)
        setPortfolioTab(config[0].key);
      }
    }
  }, [analysisResults, hideViewAll]);

  if (analysisLoading) {
    return (
      <Flex
        align="center"
        justify="center"
        style={{ height: "100%" }}
        direction="column"
        gap="3"
      >
        {loadingProgress !== undefined ? (
          <Box
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "16px",
              width: "100%",
              maxWidth: "300px",
            }}
          >
            <Spinner size="3" />
            <Box
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "8px",
                width: "100%",
              }}
            >
              <Box
                style={{
                  width: "100%",
                  height: "6px",
                  backgroundColor: "var(--gray-5)",
                  borderRadius: "3px",
                  overflow: "hidden",
                }}
              >
                <Box
                  style={{
                    width: `${loadingProgress}%`,
                    height: "100%",
                    backgroundColor: "var(--accent-9)",
                    transition: "width 0.3s ease-out",
                    borderRadius: "3px",
                  }}
                />
              </Box>
              <Text
                size="2"
                style={{
                  fontWeight: 500,
                  color: "var(--gray-11)",
                }}
              >
                Analyzing... {Math.round(loadingProgress)}%
              </Text>
            </Box>
          </Box>
        ) : (
          <>
            <Spinner size="3" />
            <Text size="3" color="gray">
              Generating analysis...
            </Text>
          </>
        )}
      </Flex>
    );
  }

  if (analysisError) {
    return (
      <Flex align="center" justify="center" style={{ minHeight: "300px" }}>
        <Box style={{ textAlign: "center" }}>
          <Text size="4" color="red" weight="medium" mb="2">
            Error
          </Text>
          <Text size="2" color="gray" ml="2">
            {analysisError}
          </Text>
        </Box>
      </Flex>
    );
  }

  if (!analysisResults) {
    return null;
  }

  const tabConfig = getTabConfiguration(analysisResults, hideViewAll);
  const isPortfolioOrFund = tabConfig.length > 0;
  const parsedSections = !isPortfolioOrFund
    ? parseInsightSections(Object.values(analysisResults).join("\n\n"))
    : null;

  return (
    <Box p="2" id="insights-content" style={{ position: "relative" }}>
      <Flex direction="column" gap="4">
        <InsightsHeader
          name={name}
          timeframe={timeframe}
          subtitle={subtitle}
          showApiCall={showApiCall}
          onApiCallToggle={onApiCallToggle}
        />
        {isPortfolioOrFund ? (
          <Flex direction="column" gap="3">
            {/* Main tabs */}
            <Flex gap="2" wrap="wrap">
              {tabConfig.map((tab) => (
                <TabButton
                  key={tab.key}
                  active={portfolioTab === tab.key}
                  onClick={() => setPortfolioTab(tab.key)}
                >
                  {tab.display}
                </TabButton>
              ))}
            </Flex>

            {/* Narrative selector for Talk Tracks */}
            {portfolioTab === "talk_tracks" && (
              <Flex align="center" gap="2">
                <Text size="2" weight="medium">
                  Narrative:
                </Text>
                <Select.Root
                  value={talkTracksSubTab}
                  onValueChange={setTalkTracksSubTab}
                >
                  <Select.Trigger placeholder="Select narrative..." />
                  <Select.Content>
                    <Select.Item value="highlights">Highlights</Select.Item>
                    <Select.Item value="moderate">Moderate</Select.Item>
                    <Select.Item value="advanced">Advanced</Select.Item>
                  </Select.Content>
                </Select.Root>
              </Flex>
            )}

            <Box>
              {portfolioTab === "talk_tracks"
                ? renderTabContent(
                    { key: talkTracksSubTab, display: talkTracksSubTab },
                    analysisResults,
                    isMobileView,
                    newsProvider,
                    symbol,
                    enableCopy,
                    disablePerformance,
                    newsTruncateWords,
                    newsLinkText
                  )
                : tabConfig.find((tab) => tab.key === portfolioTab) &&
                  renderTabContent(
                    tabConfig.find((tab) => tab.key === portfolioTab)!,
                    analysisResults,
                    isMobileView,
                    newsProvider,
                    symbol,
                    enableCopy,
                    disablePerformance,
                    newsTruncateWords,
                    newsLinkText
                  )}
            </Box>
          </Flex>
        ) : parsedSections &&
          (parsedSections.positive || parsedSections.negative) ? (
          <Flex direction="column" gap="3">
            <Flex gap="2">
              {parsedSections.positive && (
                <TabButton
                  active={activeTab === "positive"}
                  onClick={() => onTabChange("positive")}
                >
                  What&apos;s going well
                </TabButton>
              )}
              {parsedSections.negative && (
                <TabButton
                  active={activeTab === "negative"}
                  onClick={() => onTabChange("negative")}
                >
                  What&apos;s not
                </TabButton>
              )}
            </Flex>

            <Box>
              {activeTab === "positive" && parsedSections.positive && (
                <Box>
                  {parsedSections.positive
                    .split("\n")
                    .filter((line) => line.trim())
                    .map((line, index) => {
                      const isBullet = /^[\s]*[-*•]/.test(line);
                      if (isBullet) {
                        const content = line.replace(/^[\s]*[-*•]\s*/, "");
                        return (
                          <Flex key={index} gap="2" align="start" mb="1">
                            <Box
                              style={{
                                flexShrink: 0,
                                marginTop: "4px",
                              }}
                            >
                              <CircularArrowUp size={20} />
                            </Box>
                            <Box
                              style={{
                                lineHeight: 1.5,
                              }}
                            >
                              <SecurityExplainContent
                                content={content}
                                popoutNews={true}
                                newsProvider={newsProvider}
                                isMobileView={isMobileView}
                                newsTruncateWords={newsTruncateWords}
                                newsLinkText={newsLinkText}
                                enableCopy={enableCopy}
                              />
                            </Box>
                          </Flex>
                        );
                      } else if (line.trim()) {
                        return (
                          <Box key={index} mb="2">
                            <SecurityExplainContent
                              content={line}
                              popoutNews={true}
                              newsProvider={newsProvider}
                              isMobileView={isMobileView}
                              newsTruncateWords={newsTruncateWords}
                              newsLinkText={newsLinkText}
                              enableCopy={enableCopy}
                            />
                          </Box>
                        );
                      }
                      return null;
                    })}
                </Box>
              )}

              {activeTab === "negative" && parsedSections.negative && (
                <Box>
                  {parsedSections.negative
                    .split("\n")
                    .filter((line) => line.trim())
                    .map((line, index) => {
                      const isBullet = /^[\s]*[-*•]/.test(line);
                      if (isBullet) {
                        const content = line.replace(/^[\s]*[-*•]\s*/, "");
                        return (
                          <Flex key={index} gap="2" align="start" mb="1">
                            <Box
                              style={{
                                flexShrink: 0,
                                marginTop: "4px",
                              }}
                            >
                              <CircularArrowDown size={20} />
                            </Box>
                            <Box
                              style={{
                                lineHeight: 1.5,
                              }}
                            >
                              <SecurityExplainContent
                                content={content}
                                popoutNews={true}
                                newsProvider={newsProvider}
                                isMobileView={isMobileView}
                                newsTruncateWords={newsTruncateWords}
                                newsLinkText={newsLinkText}
                                enableCopy={enableCopy}
                              />
                            </Box>
                          </Flex>
                        );
                      } else if (line.trim()) {
                        return (
                          <Box key={index} mb="2">
                            <SecurityExplainContent
                              content={line}
                              popoutNews={true}
                              newsProvider={newsProvider}
                              isMobileView={isMobileView}
                              newsTruncateWords={newsTruncateWords}
                              newsLinkText={newsLinkText}
                              enableCopy={enableCopy}
                            />
                          </Box>
                        );
                      }
                      return null;
                    })}
                </Box>
              )}
            </Box>
          </Flex>
        ) : (
          <Flex direction="column" gap="4">
            {Object.entries(analysisResults).map(([key, value]) => (
              <Box key={key}>
                <SecurityExplainContent
                  content={value}
                  popoutNews={true}
                  newsProvider={newsProvider}
                  isMobileView={isMobileView}
                  isFund={isFund}
                  newsTruncateWords={newsTruncateWords}
                  newsLinkText={newsLinkText}
                  enableCopy={enableCopy}
                />
              </Box>
            ))}
          </Flex>
        )}
      </Flex>
      {traceId && (
        <Box
          style={{
            position: "fixed",
            bottom: "20px",
            right: "20px",
            zIndex: 50,
          }}
        >
          <Tooltip content={traceCopied ? "Copied!" : "Copy trace ID"}>
            <IconButton
              size="1"
              variant="ghost"
              color="gray"
              onClick={handleCopyTraceId}
              style={{
                opacity: 0.2,
                transition: "opacity 0.2s",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.opacity = "0.6";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.opacity = "0.2";
              }}
            >
              <CopyIcon />
            </IconButton>
          </Tooltip>
        </Box>
      )}
    </Box>
  );
}
